/**
 * Retroactive string sync utility.
 *
 * Scans all objects and dialogues for a game and creates any missing
 * TMS string entries for translatable fields. This is called when
 * languages are first configured on a game that already has content.
 */

import { objectsStore } from "@/lib/stores/objects/store";
import { dialogStore } from "@/lib/stores/dialogs/store";
import { createString, updateStringsItem } from "@/lib/stores/stringsStore";
import { processObjectForTranslation } from "@/lib/utils/objectParser";
import { processDialogForTranslation } from "@/lib/stores/dialogs/utils/translation";
import { apiService } from "@/lib/api-functions/api-service";

type ToastFn = (props: {
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: string;
}) => void;

/**
 * Iterate over every object and dialog for the given game and ensure that
 * translatable fields have corresponding TMS string entries.
 *
 * After processing, persists the updated string_id references back to the
 * API so they are not lost.
 */
export async function syncTranslatableStrings(
  gameId: string,
  toast?: ToastFn
): Promise<{ createdStrings: number; updatedStrings: number }> {
  let totalCreated = 0;
  let totalUpdated = 0;

  const objects = objectsStore.getData().filter((o) => o.game_id === gameId);
  const dialogs = dialogStore.getData().filter((d) => d.game_id === gameId);

  const totalItems = objects.length + dialogs.length;
  if (totalItems === 0) return { createdStrings: 0, updatedStrings: 0 };

  console.log(
    `[syncTranslatableStrings] Starting sync for game ${gameId}: ${objects.length} objects, ${dialogs.length} dialogs`
  );

  // --- Objects ---
  for (const obj of objects) {
    if (!obj.data || obj.data.length === 0) continue;

    const hasTranslatable = obj.data.some((section) =>
      section.fields?.some((field) => field.translation && field.value)
    );
    if (!hasTranslatable) continue;

    try {
      const { glossaryItem: processed, createdStrings, updatedStrings } =
        await processObjectForTranslation(
          {
            id: obj.id,
            game_id: obj.game_id,
            name: obj.name,
            type: obj.type,
            data: obj.data,
            tags: obj.tags,
            blueprint_id: obj.blueprint_id,
            version: obj.version,
            blueprint_version: obj.blueprint_version,
            identifiers: obj.identifiers,
            folder: obj.folder,
            status: obj.status,
          },
          createString,
          updateStringsItem
        );

      if (createdStrings.length > 0) {
        await apiService.updateObject({
          ...processed,
          skipVersionBump: true,
        } as any);

        const currentData = objectsStore.getData();
        const updated = currentData.map((o) =>
          o.id === obj.id ? { ...o, data: processed.data } : o
        );
        objectsStore.setData(updated);
      }

      totalCreated += createdStrings.length;
      totalUpdated += updatedStrings.length;
    } catch (err) {
      console.error(
        `[syncTranslatableStrings] Failed to sync object ${obj.id}:`,
        err
      );
    }
  }

  // --- Dialogs ---
  for (const dlg of dialogs) {
    if (!dlg.data?.sections || dlg.data.sections.length === 0) continue;

    const hasTranslatable = dlg.data.sections.some((section) =>
      section.fields?.some(
        (field) =>
          (field.translatable && field.text) ||
          field.options?.some((opt) => opt.translatable && opt.option_text)
      )
    );
    if (!hasTranslatable) continue;

    try {
      const {
        dialog: processed,
        createdStrings,
        updatedStrings,
      } = await processDialogForTranslation(
        {
          game_id: dlg.game_id,
          name: dlg.name,
          dialog_key: dlg.dialog_key,
          description: dlg.description,
          tags: dlg.tags,
          folder: dlg.folder,
          status: dlg.status,
          data: dlg.data,
        },
        createString,
        updateStringsItem
      );

      if (createdStrings.length > 0) {
        // Sync string_ids from sections to nodes before saving
        const processedData = { ...processed.data };
        if (processedData?.nodes && processedData?.sections) {
          const fieldStringIdMap = new Map<string, string>();
          const fieldOptionsMap = new Map<string, any[]>();
          for (const section of processedData.sections) {
            if (section.fields) {
              for (const field of section.fields) {
                if (field.string_id) {
                  fieldStringIdMap.set(field.id, field.string_id);
                }
                if (field.options && field.options.length > 0) {
                  fieldOptionsMap.set(field.id, field.options);
                }
              }
            }
          }
          for (const node of processedData.nodes) {
            const stringId = fieldStringIdMap.get(node.id);
            if (stringId && !node.string_id) {
              node.string_id = stringId;
            }
            const processedOptions = fieldOptionsMap.get(node.id);
            if (processedOptions && node.options) {
              for (let i = 0; i < node.options.length; i++) {
                if (processedOptions[i]?.string_id && !node.options[i].string_id) {
                  node.options[i].string_id = processedOptions[i].string_id;
                }
              }
            }
          }
        }

        await apiService.updateDialog({
          id: dlg.id,
          game_id: dlg.game_id,
          name: dlg.name,
          dialog_key: dlg.dialog_key,
          data: processedData,
        } as any);

        const currentData = dialogStore.getData();
        const updated = currentData.map((d) =>
          d.id === dlg.id ? { ...d, data: processedData } : d
        );
        dialogStore.setData(updated);
      }

      totalCreated += createdStrings.length;
      totalUpdated += updatedStrings.length;
    } catch (err) {
      console.error(
        `[syncTranslatableStrings] Failed to sync dialog ${dlg.id}:`,
        err
      );
    }
  }

  console.log(
    `[syncTranslatableStrings] Complete: ${totalCreated} created, ${totalUpdated} updated`
  );

  if (toast && totalCreated > 0) {
    toast({
      title: "Strings synced",
      description: `Created ${totalCreated} translatable string${totalCreated !== 1 ? "s" : ""} from existing content.`,
    });
  }

  return { createdStrings: totalCreated, updatedStrings: totalUpdated };
}
